import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import { X, Menu } from "lucide-react";
import logo from "../../../assets/images/jobHarvestTransparent.png";

const CinematicNav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userId = useSelector((state) => state.loginUser.userId);
  const heroRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Re-run per page: the nav outlives route changes, and only the home page has a hero.
  useEffect(() => {
    const hero = document.querySelector(".cinematic-hero");
    if (!hero) {
      // Pages without a hero (Contact, Pricing) go solid once the page scrolls,
      // so the links never sit transparent over the content.
      const onScroll = () => setIsScrolled(window.scrollY > 24);
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsScrolled(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, [pathname]);

  const navLinks = [
    { label: "How It Works", href: "/#features" },
    { label: "Why Us", href: "/#whyus" },
    { label: "Pricing", href: "/#pricing" },
  ];

  // Section links: on the home page, smooth-scroll in place; from any other
  // page (e.g. /mobile-app), SPA-navigate to "/" and let the home page animate
  // the scroll down to the hash target — no full reload, no instant jump.
  const handleSectionClick = (e, href) => {
    e.preventDefault();
    if (pathname === "/") {
      const target = document.getElementById(href.split("#")[1]);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        window.history.replaceState(null, "", href);
        return;
      }
    }
    navigate(href);
  };

  const currentQueryParams = window.location.search;

  return (
    <>
      <nav
        className={`cinematic-nav ${isScrolled ? "cinematic-nav--solid" : "cinematic-nav--transparent"}`}
      >
        <a href="/" className="cinematic-nav__logo">
          <img src={logo} alt="JobHarvest" className="cinematic-nav__logo-img" />
          <span className="cinematic-nav__logo-text">JobHarvest</span>
        </a>

        <ul className="cinematic-nav__links">
          {navLinks.map((link) => (
            <li key={link.label}>
              <a
                href={link.href}
                className="cinematic-nav__link"
                onClick={(e) => handleSectionClick(e, link.href)}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {userId ? (
          <a href="/dashboard/apply" className="cinematic-nav__cta cinematic-nav__cta-desktop">
            Dashboard
          </a>
        ) : (
          <div className="cinematic-nav__auth-group">
            <a href="/signin" className="cinematic-nav__sign-in">
              Sign In
            </a>
            <a
              href={`/signup${currentQueryParams}`}
              className="cinematic-nav__cta cinematic-nav__cta-desktop"
            >
              Get Started
            </a>
          </div>
        )}

        <button
          className="cinematic-nav__hamburger"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <Menu
            size={24}
            color={isScrolled ? "#1A1A1A" : "#F2F0E9"}
          />
        </button>
      </nav>

      {mobileOpen &&
        createPortal(
          <div className="cinematic-nav__mobile-overlay">
            <button
              className="cinematic-nav__mobile-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X size={28} />
            </button>
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  setMobileOpen(false);
                  handleSectionClick(e, link.href);
                }}
              >
                {link.label}
              </a>
            ))}
            {userId ? (
              <a href="/dashboard/apply" onClick={() => setMobileOpen(false)}>
                Dashboard
              </a>
            ) : (
              <>
                <a href="/signin" onClick={() => setMobileOpen(false)}>
                  Sign In
                </a>
                <a
                  href={`/signup${currentQueryParams}`}
                  onClick={() => setMobileOpen(false)}
                  style={{ color: "#1abc9c" }}
                >
                  Get Started
                </a>
              </>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default CinematicNav;
